// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
/**
 * Users Management - ISO 27001 A.9.2.1 User registration
 * Supports multi-company role assignment
 */
import {
  Plus,
  Trash2,
  UserX,
  Edit3,
  Users,
  Shield,
  X,
  UserCircle,
  Mail,
  Calendar,
  Building2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notify } from '@/stores/notificationStore';
import { confirm } from '@/stores/confirmStore';
import { toFrench } from '@/utils/dateUtils';
import { usersAPI, companiesAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import { Button, Spinner, Badge } from '@/components/ui-v2';
import { PasswordStrengthIndicator } from '@/features/auth';

// CompanyRole values from Prisma schema
type CompanyRole = 'OWNER' | 'ADMIN' | 'MANAGER' | 'ACCOUNTANT' | 'MEMBER' | 'VIEWER';

interface CompanyOption {
  id: string;
  name: string;
}

interface UserCompanyAccess {
  companyId: string;
  companyName: string;
  role: CompanyRole;
}

const COMPANY_ROLES: { value: CompanyRole; labelKey: string; color: string }[] = [
  { value: 'OWNER', labelKey: 'admin.users.companyRoles.OWNER', color: 'bg-purple-100 text-purple-700' },
  { value: 'ADMIN', labelKey: 'admin.users.companyRoles.ADMIN', color: 'bg-blue-100 text-blue-700' },
  { value: 'MANAGER', labelKey: 'admin.users.companyRoles.MANAGER', color: 'bg-teal-100 text-teal-700' },
  { value: 'ACCOUNTANT', labelKey: 'admin.users.companyRoles.ACCOUNTANT', color: 'bg-amber-100 text-amber-700' },
  { value: 'MEMBER', labelKey: 'admin.users.companyRoles.MEMBER', color: 'bg-gray-100 text-gray-700' },
  { value: 'VIEWER', labelKey: 'admin.users.companyRoles.VIEWER', color: 'bg-gray-100 text-gray-600' },
];

export const UsersManagement: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const [showManageAccess, setShowManageAccess] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userCompanyAccesses, setUserCompanyAccesses] = useState<UserCompanyAccess[]>([]);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  const [newUser, setNewUser] = useState({
    fullName: '',
    username: '',
    email: '',
    avatar: '',
    password: '',
    role: 'MEMBER',
    companyId: '',
    companyRole: 'MEMBER' as CompanyRole,
    userType: 'company' as 'tenant' | 'company', // Par défaut: utilisateur société
  });

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none cursor-pointer text-sm`;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load users and companies in parallel
      const [usersResponse, companiesResponse] = await Promise.all([
        usersAPI.getAll(),
        companiesAPI.getAll(),
      ]);
      const usersList = usersResponse.data?.users || [];
      logger.info('Users loaded:', usersList);
      setUsers(usersList);
      // companiesAPI.getAll() retourne json.data (le tableau) directement
      // Mais parfois peut retourner l'objet complet si json.data est undefined
      logger.info('Companies API response:', companiesResponse);
      const companiesList = Array.isArray(companiesResponse)
        ? companiesResponse
        : (companiesResponse?.companies || companiesResponse?.data || []);
      logger.info('Parsed companies list:', companiesList);
      const mapped = companiesList.map((c: any) => ({ id: c.id, name: c.legalName || c.name }));
      logger.info('Mapped companies:', mapped);
      setCompanies(mapped);
    } catch (error) {
      logger.error('Erreur chargement données:', error);
      setUsers([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data?.users || []);
    } catch (error) {
      logger.error('Erreur chargement utilisateurs:', error);
      setUsers([]);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newUser.fullName || !newUser.email || !newUser.password) {
      notify.warning(t('admin.users.requiredFieldsMissing'), t('admin.users.requiredFieldsMissingTitle'));
      return;
    }

    // Pour un utilisateur société, la société est obligatoire
    if (newUser.userType === 'company' && !newUser.companyId) {
      notify.warning(t('admin.users.companyRequired'), t('admin.users.companyRequiredTitle'));
      return;
    }

    try {
      // Architecture Multi-Tenant : toujours TenantUser + CompanyUser si company sélectionnée
      // - Utilisateur Métier : TenantRole=MEMBER + CompanyRole=choix
      // - Admin Tenant : TenantRole=ADMIN/OWNER + CompanyRole optionnel
      const userData = {
        fullName: newUser.fullName,
        username: newUser.username || undefined,
        email: newUser.email,
        avatar: newUser.avatar || undefined,
        password: newUser.password,
        // TenantRole : MEMBER pour utilisateur métier, sinon le rôle sélectionné
        role: newUser.userType === 'company' ? 'MEMBER' : newUser.role,
        // CompanyRole si une société est sélectionnée
        ...(newUser.companyId && {
          companyId: newUser.companyId,
          companyRole: newUser.companyRole,
        }),
      };
      logger.info('Creating user with data:', userData);
      await usersAPI.create(userData);
      notify.success(t('admin.users.userCreatedSuccess'), t('admin.users.creationSuccess'));
      setShowAddUser(false);
      setNewUser({
        fullName: '',
        username: '',
        email: '',
        avatar: '',
        password: '',
        role: 'MEMBER',
        companyId: '',
        companyRole: 'MEMBER',
        userType: 'company',
      });
      loadUsers();
    } catch (error: any) {
      notify.error(error.message || t('admin.users.creationError'), t('common.errors.saveFailed'));
    }
  };

  const handleEditRole = (user: any) => {
    setSelectedUser(user);
    setShowEditRole(true);
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser) return;

    try {
      await usersAPI.update(selectedUser.id, {
        fullName: selectedUser.fullName,
        email: selectedUser.email,
        role: selectedUser.role,
      });
      notify.success(t('admin.users.roleUpdatedSuccess'), t('admin.users.roleUpdateSuccess'));
      setShowEditRole(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      notify.error(t('admin.users.roleUpdateError'), t('common.errors.saveFailed'));
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const confirmed = await confirm({
      title: t('admin.users.deleteConfirmTitle'),
      message: t('admin.users.deleteConfirmMessage'),
      confirmText: t('common.actions.delete'),
      cancelText: t('common.actions.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await usersAPI.delete(userId);
      notify.success(t('admin.users.userDeletedSuccess'), t('admin.users.deletionSuccess'));
      loadUsers();
    } catch (error) {
      notify.error(t('common.errors.deleteFailed'), t('common.errors.deleteFailed'));
    }
  };

  // Ouvrir le modal de gestion des accès sociétés
  const handleManageAccess = async (user: any) => {
    setSelectedUser(user);
    setShowManageAccess(true);
    setLoadingAccess(true);

    try {
      // Charger les accès de l'utilisateur pour chaque société
      const accesses: UserCompanyAccess[] = [];
      for (const company of companies) {
        try {
          const users = await companiesAPI.getCompanyUsers(company.id);
          const userAccess = users.find((u: any) => u.userId === user.id);
          if (userAccess) {
            accesses.push({
              companyId: company.id,
              companyName: company.name,
              role: userAccess.role as CompanyRole,
            });
          }
        } catch {
          // Ignorer les erreurs par société
        }
      }
      setUserCompanyAccesses(accesses);
    } catch (error) {
      logger.error('Erreur chargement accès:', error);
      notify.error(t('admin.users.loadAccessError'), t('common.errors.loadFailed'));
    } finally {
      setLoadingAccess(false);
    }
  };

  // Ajouter un accès société
  const handleAddCompanyAccess = async (companyId: string, role: CompanyRole) => {
    if (!selectedUser) return;

    try {
      setSavingAccess(true);
      await companiesAPI.addCompanyUser(companyId, {
        userId: selectedUser.id,
        role,
      });

      const company = companies.find(c => c.id === companyId);
      setUserCompanyAccesses(prev => [
        ...prev,
        { companyId, companyName: company?.name || '', role },
      ]);
      notify.success(t('admin.users.accessAddedSuccess'), t('common.success'));
    } catch (error: any) {
      notify.error(error.message || t('admin.users.accessAddError'), t('common.errors.saveFailed'));
    } finally {
      setSavingAccess(false);
    }
  };

  // Modifier le rôle dans une société
  const handleUpdateCompanyRole = async (companyId: string, newRole: CompanyRole) => {
    if (!selectedUser) return;

    try {
      setSavingAccess(true);
      await companiesAPI.updateCompanyUserRole(companyId, selectedUser.id, newRole);
      setUserCompanyAccesses(prev =>
        prev.map(a => a.companyId === companyId ? { ...a, role: newRole } : a)
      );
      notify.success(t('admin.users.roleModifiedSuccess'), t('common.success'));
    } catch (error: any) {
      notify.error(error.message || t('admin.users.roleModifyError'), t('common.errors.saveFailed'));
    } finally {
      setSavingAccess(false);
    }
  };

  // Retirer un accès société
  const handleRemoveCompanyAccess = async (companyId: string) => {
    if (!selectedUser) return;

    const confirmed = await confirm({
      title: t('admin.users.removeAccessConfirmTitle'),
      message: t('admin.users.removeAccessConfirmMessage'),
      confirmText: t('common.actions.remove'),
      cancelText: t('common.actions.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      setSavingAccess(true);
      await companiesAPI.removeCompanyUser(companyId, selectedUser.id);
      setUserCompanyAccesses(prev => prev.filter(a => a.companyId !== companyId));
      notify.success(t('admin.users.accessRemovedSuccess'), t('common.success'));
    } catch (error: any) {
      notify.error(error.message || t('admin.users.accessRemoveError'), t('common.errors.deleteFailed'));
    } finally {
      setSavingAccess(false);
    }
  };

  // Helper pour obtenir le label du rôle
  const getRoleLabel = (role: CompanyRole) => {
    return t(`admin.users.companyRoles.${role}`);
  };

  const getRoleColor = (role: CompanyRole) => {
    return COMPANY_ROLES.find(r => r.value === role)?.color || 'bg-gray-100 text-gray-700';
  };

  const getRoleBadge = (user: any) => {
    // Architecture Multi-Tenant :
    // - userType='company' : Utilisateur métier (TenantRole=MEMBER + CompanyRole)
    // - userType='tenant' : Admin tenant (TenantRole=ADMIN/OWNER)

    if (user.userType === 'company') {
      // Afficher le CompanyRole pour les utilisateurs métier
      const companyRoleConfig: Record<string, { variant: 'success' | 'info' | 'warning' | 'alert' | 'pending'; labelKey: string }> = {
        OWNER: { variant: 'alert', labelKey: 'admin.users.companyRoleBadge.OWNER' },
        ADMIN: { variant: 'info', labelKey: 'admin.users.companyRoleBadge.ADMIN' },
        MANAGER: { variant: 'info', labelKey: 'admin.users.companyRoleBadge.MANAGER' },
        ACCOUNTANT: { variant: 'warning', labelKey: 'admin.users.companyRoleBadge.ACCOUNTANT' },
        MEMBER: { variant: 'success', labelKey: 'admin.users.companyRoleBadge.MEMBER' },
        VIEWER: { variant: 'pending', labelKey: 'admin.users.companyRoleBadge.VIEWER' },
      };
      const config = companyRoleConfig[user.companyRole] || { variant: 'pending' as const, labelKey: 'admin.users.businessLabel' };
      return (
        <div className="flex items-center gap-1">
          <Badge variant={config.variant}>{t(config.labelKey)}</Badge>
          <span className="text-xs text-gray-400">{t('admin.users.companyLabel')}</span>
        </div>
      );
    }

    // Afficher le TenantRole pour les admins tenant
    const tenantRoleConfig: Record<string, { variant: 'success' | 'info' | 'warning' | 'alert' | 'pending'; labelKey: string }> = {
      OWNER: { variant: 'alert', labelKey: 'admin.users.tenantRoleBadge.OWNER' },
      ADMIN: { variant: 'info', labelKey: 'admin.users.tenantRoleBadge.ADMIN' },
      MEMBER: { variant: 'success', labelKey: 'admin.users.tenantRoleBadge.MEMBER' },
    };

    const config = tenantRoleConfig[user.role] || { variant: 'pending' as const, labelKey: 'admin.users.defaultLabel' };
    return <Badge variant={config.variant}>{t(config.labelKey)}</Badge>;
  };

  if (loading) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Users className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.users.management')}</h1>
            <p className="text-gray-500 mt-0.5">{t('admin.users.countInTeam', { count: users.length })}</p>
          </div>
        </div>

        <Button
          variant="secondary"
          onClick={() => setShowAddUser(true)}
          icon={<Plus className="w-4 h-4" />}
        >
          {t('admin.users.addUser')}
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Shield className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">{t('admin.users.accessManagement')}</h4>
            <p className="text-sm text-teal-800 mt-1">
              {t('admin.users.accessManagementDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Modal Édition de Rôle */}
      {showEditRole && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-white/50">
            {/* Header modal */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Edit3 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{t('admin.users.editRoleTitle')}</h3>
                </div>
                <button
                  onClick={() => {
                    setShowEditRole(false);
                    setSelectedUser(null);
                  }}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Contenu modal */}
            <form onSubmit={handleUpdateRole} className="p-6 space-y-6">
              <div className="bg-gray-50/50 rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-2xl flex items-center justify-center">
                    <span className="text-teal-600 font-bold text-lg">
                      {(selectedUser.fullName || selectedUser.email)?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      {selectedUser.fullName || selectedUser.email}
                    </div>
                    <div className="text-sm text-gray-500">{selectedUser.email}</div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.users.tenantRole')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedUser.role}
                  onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                  className={selectClasses}
                  required
                >
                  <option value="MEMBER">{t('admin.users.tenantRoleOptions.MEMBER')}</option>
                  <option value="ADMIN">{t('admin.users.tenantRoleOptions.ADMIN')}</option>
                  <option value="OWNER">{t('admin.users.tenantRoleOptions.OWNER')}</option>
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  {t('admin.users.tenantRoleHintEdit')}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowEditRole(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1"
                >
                  {t('common.actions.cancel')}
                </Button>
                <Button type="submit" variant="secondary" className="flex-1">
                  {t('common.actions.edit')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ajout Utilisateur */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden border border-white/50">
            {/* Header modal */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <UserCircle className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{t('admin.users.newUserTitle')}</h3>
                </div>
                <button
                  onClick={() => {
                    setShowAddUser(false);
                    setNewUser({
                      fullName: '',
                      username: '',
                      email: '',
                      avatar: '',
                      password: '',
                      role: 'MEMBER',
                      companyId: '',
                      companyRole: 'MEMBER',
                      userType: 'company',
                    });
                  }}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Contenu modal */}
            <form onSubmit={handleAddUser} className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
              {/* Type d'utilisateur - Architecture Multi-Tenant */}
              <div className="bg-gray-50/50 rounded-2xl p-4 space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  {t('admin.users.userType')} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setNewUser({ ...newUser, userType: 'company', role: 'MEMBER' })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      newUser.userType === 'company'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className={`w-4 h-4 ${newUser.userType === 'company' ? 'text-teal-600' : 'text-gray-400'}`} />
                      <span className={`font-medium text-sm ${newUser.userType === 'company' ? 'text-teal-700' : 'text-gray-700'}`}>
                        {t('admin.users.companyUser')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('admin.users.companyUserDesc')}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewUser({ ...newUser, userType: 'tenant', role: 'ADMIN' })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      newUser.userType === 'tenant'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className={`w-4 h-4 ${newUser.userType === 'tenant' ? 'text-teal-600' : 'text-gray-400'}`} />
                      <span className={`font-medium text-sm ${newUser.userType === 'tenant' ? 'text-teal-700' : 'text-gray-700'}`}>
                        {t('admin.users.tenantAdmin')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('admin.users.tenantAdminDesc')}
                    </p>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.users.fullName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newUser.fullName}
                  onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                  className={inputClasses}
                  placeholder={t('admin.users.placeholders.fullName')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.users.username')}
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className={inputClasses}
                  placeholder={t('admin.users.placeholders.username')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('admin.users.usernameOptionalHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.labels.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className={inputClasses}
                  placeholder={t('admin.users.placeholders.email')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.users.avatarUrl')}
                </label>
                <input
                  type="url"
                  value={newUser.avatar}
                  onChange={(e) => setNewUser({ ...newUser, avatar: e.target.value })}
                  className={inputClasses}
                  placeholder={t('admin.users.placeholders.avatarUrl')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('admin.users.avatarOptionalHint')}</p>
              </div>

              {/* Rôle Tenant - uniquement pour Admin Tenant */}
              {newUser.userType === 'tenant' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin.users.tenantRole')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className={selectClasses}
                    required
                  >
                    <option value="MEMBER">{t('admin.users.tenantRoleOptions.MEMBER')}</option>
                    <option value="ADMIN">{t('admin.users.tenantRoleOptions.ADMIN')}</option>
                    <option value="OWNER">{t('admin.users.tenantRoleOptions.OWNER')}</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.users.tenantRoleHint')}
                  </p>
                </div>
              )}

              {/* Company Assignment - ISO 27001 A.9.2.3 */}
              {/* Toujours afficher pour userType company, optionnel pour tenant */}
              {(newUser.userType === 'company' || companies.length > 0) && (
                <div className="bg-teal-50/30 border border-teal-200/50 rounded-2xl p-4 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-teal-600" />
                    <span className="text-sm font-semibold text-teal-800">
                      {newUser.userType === 'company' ? t('admin.users.companySection') : t('admin.users.companySectionOptional')}
                    </span>
                  </div>

                  {/* Message d'erreur si pas de sociétés pour utilisateur société */}
                  {companies.length === 0 && newUser.userType === 'company' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <p className="text-sm text-amber-800">
                        {t('admin.users.noCompaniesWarning')}
                      </p>
                    </div>
                  )}

                  {companies.length > 0 && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('admin.users.company')} {newUser.userType === 'company' && <span className="text-red-500">*</span>}
                        </label>
                        <select
                          value={newUser.companyId}
                          onChange={(e) => setNewUser({ ...newUser, companyId: e.target.value })}
                          className={selectClasses}
                          required={newUser.userType === 'company'}
                        >
                          {newUser.userType === 'tenant' && (
                            <option value="">{t('admin.users.noCompanyOption')}</option>
                          )}
                          {newUser.userType === 'company' && !newUser.companyId && (
                            <option value="">{t('admin.users.selectCompanyPlaceholder')}</option>
                          )}
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {newUser.userType === 'company'
                            ? t('admin.users.companyAccessHintRequired')
                            : t('admin.users.companyAccessHintOptional')
                          }
                        </p>
                      </div>

                      {(newUser.companyId || newUser.userType === 'company') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('admin.users.companyRole')} <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={newUser.companyRole}
                            onChange={(e) => setNewUser({ ...newUser, companyRole: e.target.value as CompanyRole })}
                            className={selectClasses}
                            required
                          >
                            <option value="VIEWER">{t('admin.users.companyRoleOptions.VIEWER')}</option>
                            <option value="MEMBER">{t('admin.users.companyRoleOptions.MEMBER')}</option>
                            <option value="ACCOUNTANT">{t('admin.users.companyRoleOptions.ACCOUNTANT')}</option>
                            <option value="MANAGER">{t('admin.users.companyRoleOptions.MANAGER')}</option>
                            <option value="ADMIN">{t('admin.users.companyRoleOptions.ADMIN')}</option>
                            <option value="OWNER">{t('admin.users.companyRoleOptions.OWNER')}</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {t('admin.users.companyRoleHint')}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.users.password')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className={inputClasses}
                  placeholder={t('admin.users.passwordPlaceholder')}
                  required
                  minLength={12}
                />
                {/* Password Strength Indicator - ISO 27001 A.9.4.3 */}
                <PasswordStrengthIndicator
                  password={newUser.password}
                  userInputs={[newUser.email, newUser.fullName]}
                  isAdmin={newUser.role === 'ADMIN' || newUser.role === 'OWNER'}
                  showRequirements={true}
                  showSuggestions={true}
                  className="mt-3"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowAddUser(false);
                    setNewUser({
                      fullName: '',
                      username: '',
                      email: '',
                      avatar: '',
                      password: '',
                      role: 'MEMBER',
                      companyId: '',
                      companyRole: 'MEMBER',
                      userType: 'company',
                    });
                  }}
                  className="flex-1"
                >
                  {t('common.actions.cancel')}
                </Button>
                <Button type="submit" variant="secondary" className="flex-1">
                  {t('common.actions.create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Liste des Utilisateurs */}
      {users.length === 0 ? (
        <div className={cardClasses}>
          <div className="text-center py-8">
            <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
              <UserX className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.users.noUsers')}</h3>
            <p className="text-gray-600 mb-6">
              {t('admin.users.startAddMembers')}
            </p>
            <Button
              variant="secondary"
              onClick={() => setShowAddUser(true)}
              icon={<Plus className="w-4 h-4" />}
            >
              {t('admin.users.addUser')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={`${cardClasses} overflow-hidden p-0`}>
          <div className="px-6 py-4 border-b border-white/30">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              {t('admin.users.team')}
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/30 border-b border-white/30">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.users.userColumn')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.users.emailColumn')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.users.tenantRoleColumn')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.users.companyAccessColumn')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.users.createdAtColumn')}
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.users.actionsColumn')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/20">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-white/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-teal-100 rounded-2xl flex items-center justify-center">
                          <span className="text-teal-600 font-semibold">
                            {(user.fullName || user.email)?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.fullName || user.email}
                          </div>
                          {user.username && (
                            <div className="text-xs text-gray-500">@{user.username}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getRoleBadge(user)}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleManageAccess(user)}
                        className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 hover:bg-teal-50 px-3 py-1.5 rounded-xl transition-colors"
                      >
                        <Building2 className="w-4 h-4" />
                        <span>
                          {user.companyName ? (
                            <span className="font-medium">{user.companyName}</span>
                          ) : (
                            <span className="text-gray-400">{t('admin.users.manageAccess')}</span>
                          )}
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {toFrench(user.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Utilisateur métier (userType=company) : gestion accès société */}
                        {user.userType === 'company' && (
                          <>
                            <button
                              onClick={() => handleManageAccess(user)}
                              className="p-2 text-teal-600 hover:bg-teal-50 rounded-xl transition-colors"
                              title={t('admin.users.manageCompanyAccess')}
                            >
                              <Building2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                              title={t('common.actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {/* Admin Tenant (ADMIN) : édition du rôle tenant autorisée */}
                        {user.userType === 'tenant' && user.role === 'ADMIN' && (
                          <>
                            <button
                              onClick={() => handleEditRole(user)}
                              className="p-2 text-teal-600 hover:bg-teal-50 rounded-xl transition-colors"
                              title={t('admin.users.editTenantRole')}
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                              title={t('common.actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {/* Propriétaire : pas de modification ni suppression */}
                        {user.userType === 'tenant' && user.role === 'OWNER' && (
                          <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1.5 rounded-xl">
                            {t('admin.users.owner')}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Gestion des Accès Sociétés */}
      {showManageAccess && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden border border-white/50">
            {/* Header modal */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('admin.users.companyAccess')}</h3>
                    <p className="text-white/80 text-sm">{selectedUser.fullName || selectedUser.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowManageAccess(false);
                    setSelectedUser(null);
                    setUserCompanyAccesses([]);
                  }}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Contenu modal */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {loadingAccess ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="lg" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Liste des accès actuels */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('admin.users.currentAccess')}</h4>
                    {userCompanyAccesses.length === 0 ? (
                      <div className="text-center py-6 bg-gray-50/50 rounded-2xl">
                        <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">{t('admin.users.noCompanyAccess')}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {userCompanyAccesses.map((access) => (
                          <div
                            key={access.companyId}
                            className="flex items-center justify-between p-3 bg-white/50 border border-white/50 rounded-xl"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-teal-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{access.companyName}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleColor(access.role)}`}>
                                  {getRoleLabel(access.role)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                value={access.role}
                                onChange={(e) => handleUpdateCompanyRole(access.companyId, e.target.value as CompanyRole)}
                                disabled={savingAccess || access.role === 'OWNER'}
                                className="text-xs px-2 py-1 border border-gray-200 rounded-lg bg-white"
                              >
                                {COMPANY_ROLES.filter(r => r.value !== 'OWNER').map((role) => (
                                  <option key={role.value} value={role.value}>
                                    {t(role.labelKey)}
                                  </option>
                                ))}
                              </select>
                              {access.role !== 'OWNER' && (
                                <button
                                  onClick={() => handleRemoveCompanyAccess(access.companyId)}
                                  disabled={savingAccess}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title={t('admin.users.removeAccess')}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Ajouter un accès */}
                  {companies.filter(c => !userCompanyAccesses.some(a => a.companyId === c.id)).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('admin.users.addAccess')}</h4>
                      <div className="flex gap-2">
                        <select
                          id="newCompanySelect"
                          className={`flex-1 ${selectClasses}`}
                          defaultValue=""
                        >
                          <option value="" disabled>{t('admin.users.selectCompany')}</option>
                          {companies
                            .filter(c => !userCompanyAccesses.some(a => a.companyId === c.id))
                            .map((company) => (
                              <option key={company.id} value={company.id}>
                                {company.name}
                              </option>
                            ))}
                        </select>
                        <select
                          id="newRoleSelect"
                          className={selectClasses}
                          defaultValue="MEMBER"
                          style={{ width: '140px' }}
                        >
                          {COMPANY_ROLES.filter(r => r.value !== 'OWNER').map((role) => (
                            <option key={role.value} value={role.value}>
                              {t(role.labelKey)}
                            </option>
                          ))}
                        </select>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={savingAccess}
                          onClick={() => {
                            const companySelect = document.getElementById('newCompanySelect') as HTMLSelectElement;
                            const roleSelect = document.getElementById('newRoleSelect') as HTMLSelectElement;
                            if (companySelect.value) {
                              handleAddCompanyAccess(companySelect.value, roleSelect.value as CompanyRole);
                              companySelect.value = '';
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowManageAccess(false);
                  setSelectedUser(null);
                  setUserCompanyAccesses([]);
                }}
                className="w-full"
              >
                {t('common.actions.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
